import { Platform } from './platform';
import { getWebTVInfo } from './tv/webTVInfo';

interface VastParamsProps {
  adTagUrl: string;
  duration?: number;
}

export const modifyVastUrl = (vastUrl: string | undefined, program: CmsProgram) => {
  if (!vastUrl) return undefined;

  const url = new URL(vastUrl);
  url.searchParams.set('cmsid', '2688500');
  url.searchParams.set('vid', program._id);

  return url
    .toString()
    .replace(/iu=([^&]+)/, (_, original) => `iu=${program.ads?.adunit?.val ?? original}`);
};

/**
 * Returns the ID type to be used for AD SDKs based on the detected TV vendor (IDTYPE)
 * @function getIdType
 * @returns {string}
 */
const getIdType = (): string => {
  if (Platform.isSimulatedTV || !Platform.isWebTV) return '';

  const tvInfo = getWebTVInfo();
  return tvInfo.tvIdType ?? '';
};

/**
 * Returns the RDID value to be used for AD SDKs (TIFA, LGUDID, DPID, etc.)
 * @function getRdid
 * @returns {string}
 */
const getRdid = (): string => {
  if (Platform.isSimulatedTV || !Platform.isWebTV) return '';

  const tvInfo = getWebTVInfo();
  return tvInfo.rdid ?? '';
};

/**
 * Returns the LAT (Limit Ad Tracking) flag for the detected TV as a string.
 *
 * - Samsung: returns "0" or "1" when available
 *   - 0 → LAT disable
 *   - 1 → LAT active
 * - LG: returns "0" (not available)
 * - Simulated / non-web TV: returns ""
 *
 * @function getIsLatEnabled
 * @returns {string} "0" | "1" | ""
 */
const getIsLatEnabled = () => {
  if (Platform.isSimulatedTV || !Platform.isWebTV) return '';

  const tvInfo = getWebTVInfo();

  const samsungIsLat = tvInfo?.samsung?.isLatEnabled;
  if (typeof samsungIsLat === 'boolean') return samsungIsLat ? '1' : '0';

  return '0';
};

/**
 * Returns the Store ID for the AD SDK based on the detected TV vendor (MSID)
 * @function getMsid
 * @returns {string} Store ID of vender
 */
const getMsid = (): string => {
  if (Platform.isSimulatedTV || !Platform.isWebTV) return '';

  const tvInfo = getWebTVInfo();
  return tvInfo.msid ?? '';
};

/**
 * Returns the AN (App Name) parameter for the IMA SDK
 * based on the detected TV vendor.
 * @function getAn
 * @returns {string}
 * - `"com.bandplay.tizen"` → AN for Samsung TVs
 * - `"com.band.app"` → AN for LG TVs
 * - `""` → Default for unknown or unsupported vendors
 */
const getAn = (): string => {
  if (Platform.isSimulatedTV || !Platform.isWebTV) return '';

  switch (Platform.TVVendor) {
    case 'Samsung':
      return 'com.bandplay.tizen';
    case 'LG':
      return 'com.band.app';
    default:
      return '';
  }
};

/**
 * Appends the current page URL as the "url" query parameter to the provided adTagUrl.
 * This helps buyers identify the context of the ad request, as required by Google IMA.
 *
 * @param {Object} params - Parameters object
 * @param {string} params.adTagUrl - The base ad tag URL used in the IMA AdsRequest.
 * @param {number} params.duration - The duration of the ad in seconds.
 *
 * @returns {string | undefined} The updated ad tag URL with the "url" parameter appended.
 */
export const modifyVastUrlCTV = ({ adTagUrl, duration }: VastParamsProps) => {
  if (!adTagUrl) return;

  try {
    const tag = new URL(adTagUrl);

    tag.searchParams.set('env', 'vp');
    tag.searchParams.set('gdfp_req', '1');
    tag.searchParams.set('plcmt', '1');
    tag.searchParams.set('vpa', 'click');
    tag.searchParams.set('vpmute', '0');

    tag.searchParams.set('idtype', getIdType());
    tag.searchParams.set('rdid', getRdid());
    tag.searchParams.set('is_lat', getIsLatEnabled());

    tag.searchParams.set('msid', getMsid());
    tag.searchParams.set('an', getAn());

    const mediaDuration = duration?.toString() ?? '';
    tag.searchParams.set('vid_d', mediaDuration);

    tag.searchParams.set('dth', '5');
    tag.searchParams.set('hl', 'pt');
    tag.searchParams.set('vpos', 'preroll');

    const currentUrl =
      typeof window !== 'undefined' && window.location?.href ? window.location.href : 'unknown';
    tag.searchParams.set('url', encodeURIComponent(currentUrl));

    return tag.toString();
  } catch (err) {
    console.warn('Failed to append "url" parameter to adTagUrl:', err);
    return adTagUrl;
  }
};
